import BotonManualHeader from "../../components/BotonManualHeader";
import React, { useState, useEffect } from "react";
import {
    Container,
    Card,
    Table,
    Badge,
    Button,
    Modal,
    Form,
    Row,
    Col,
    Alert,
    Spinner,
    OverlayTrigger,
    Tooltip
} from "react-bootstrap";
import {
    collection,
    query,
    where,
    getDocs,
    doc,
    updateDoc,
    serverTimestamp,
    orderBy,
    limit,
    writeBatch,
    getFirestore
} from "firebase/firestore";
import {
    FaCheck,
    FaTimes,
    FaEye,
    FaUserCheck,
    FaFilter,
    FaClipboardList,
    FaSync,
    FaCheckDouble,
    FaBan,
    FaSearch
} from "react-icons/fa";
import Swal from "sweetalert2";
import firebaseApp from "../../firebase/credenciales";
import { notificarAprobacion, notificarRechazo } from "../../services/notificacionesService";
import { logBusinessAction } from "../../services/AuditService";

const db = getFirestore(firebaseApp);

const TIPO_LABELS = {
    "DescansoMedico": "Descanso Médico",
    "Permisos": "Permiso",
    "Paternidad": "Paternidad",
    "Maternidad": "Maternidad",
    "Vacaciones": "Vacaciones",
    "DescansoRegimen": "Descanso por Régimen",
    "Otros": "Otros"
};

function AdminAusencias({ usuario }) {
    const [solicitudes, setSolicitudes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [primeraVez, setPrimeraVez] = useState(true);
    const [totalRegistros, setTotalRegistros] = useState(0);

    // Filtros optimizados
    const getFechaHaceXDias = (dias) => {
        const fecha = new Date();
        fecha.setDate(fecha.getDate() - dias);
        return fecha.toISOString().split('T')[0];
    };

    const [filtros, setFiltros] = useState({
        estado: "pendiente",
        fechaDesde: getFechaHaceXDias(30),
        fechaHasta: "",
        empleado: ""
    });

    // Estados de selección múltiple
    const [seleccionados, setSeleccionados] = useState(new Set());
    const [seleccionarTodos, setSeleccionarTodos] = useState(false);

    // Estados de modal y acciones
    const [showModal, setShowModal] = useState(false);
    const [solicitudSeleccionada, setSolicitudSeleccionada] = useState(null);
    const [motivoRechazo, setMotivoRechazo] = useState("");
    const [procesando, setProcesando] = useState(false);

    useEffect(() => {
        cargarSolicitudes();
    }, [usuario.ruc]);

    // Limpiar selección cuando cambian filtros
    useEffect(() => {
        setSeleccionados(new Set());
        setSeleccionarTodos(false);
    }, [filtros]);

    const cargarSolicitudes = async () => {
        setLoading(true);
        try {
            const solicitudesRef = collection(db, "solicitudes_ausencia");
            const ruc = usuario.ruc || usuario.rucEmpresa;

            let queryConstraints = [
                where("rucEmpresa", "==", ruc),
                orderBy("createdAt", "desc"),
                limit(100)
            ];

            if (filtros.estado) {
                queryConstraints.splice(1, 0, where("estado", "==", filtros.estado));
            }

            const q = query(solicitudesRef, ...queryConstraints);
            const querySnapshot = await getDocs(q);

            let data = [];
            let count = 0;

            querySnapshot.forEach(doc => {
                const item = { id: doc.id, ...doc.data() };
                count++;

                if (filtros.fechaDesde) {
                    const fechaItem = new Date(item.createdAt?.toDate ? item.createdAt.toDate() : item.createdAt);
                    if (fechaItem < new Date(filtros.fechaDesde)) return;
                }

                if (filtros.empleado &&
                    !item.nombreEmpleado.toLowerCase().includes(filtros.empleado.toLowerCase())) return;

                data.push(item);
            });

            setSolicitudes(data);
            setTotalRegistros(count);
            setPrimeraVez(false);

            if (primeraVez && data.length === 0) {
                // Silent info
            }

        } catch (error) {
            console.error("Error cargando solicitudes:", error);
            Swal.fire("Error", "No se pudieron cargar las solicitudes", "error");
        } finally {
            setLoading(false);
        }
    };

    const toggleSeleccion = (id) => {
        const nuevos = new Set(seleccionados);
        if (nuevos.has(id)) nuevos.delete(id);
        else nuevos.add(id);
        setSeleccionados(nuevos);
    };

    const toggleSeleccionarTodos = () => {
        if (seleccionarTodos) {
            setSeleccionados(new Set());
        } else {
            const ids = solicitudes
                .filter(s => s.estado === "pendiente")
                .map(s => s.id);
            setSeleccionados(new Set(ids));
        }
        setSeleccionarTodos(!seleccionarTodos);
    };

    const aprobarSolicitud = async (solicitud) => {
        try {
            setProcesando(true);
            const docRef = doc(db, "solicitudes_ausencia", solicitud.id);

            await updateDoc(docRef, {
                estado: "aprobado",
                aprobadoPor: usuario.email,
                fechaAprobacion: serverTimestamp()
            });

            await notificarAprobacion(
                solicitud.empleadoDni,
                solicitud.nombreEmpleado,
                solicitud.tipo,
                usuario.email,
                usuario.ruc || usuario.rucEmpresa
            );

            // AUDITORÍA
            try {
                await logBusinessAction("EVALUACION_SOLICITUD_AUSENCIA", "GestionAusencias", {
                    dniEditado: solicitud.empleadoDni,
                    cambiosRealizados: {
                        estado: { anterior: "pendiente", nuevo: "aprobado" }
                    },
                    tipoAusencia: solicitud.tipo,
                    idSolicitud: solicitud.id
                }, usuario);
            } catch (e) {
                console.warn("Error al registrar auditoría:", e);
            }

            Swal.fire({
                title: "Aprobada",
                text: "La solicitud ha sido aprobada correctamente",
                icon: "success",
                timer: 2000,
                showConfirmButton: false
            });
            cargarSolicitudes();
            setShowModal(false);
        } catch (error) {
            console.error(error);
            Swal.fire("Error", "No se pudo aprobar la solicitud", "error");
        } finally {
            setProcesando(false);
        }
    };

    const rechazarSolicitud = async (solicitud) => {
        if (!motivoRechazo.trim()) {
            Swal.fire("Atención", "Debe ingresar un motivo de rechazo", "warning");
            return;
        }

        try {
            setProcesando(true);
            const docRef = doc(db, "solicitudes_ausencia", solicitud.id);

            await updateDoc(docRef, {
                estado: "rechazado",
                rechazadoPor: usuario.email,
                fechaRechazo: serverTimestamp(),
                motivoRechazo: motivoRechazo
            });

            await notificarRechazo(
                solicitud.empleadoDni,
                solicitud.nombreEmpleado,
                solicitud.tipo,
                usuario.email,
                motivoRechazo,
                usuario.ruc || usuario.rucEmpresa
            );

            // AUDITORÍA
            try {
                await logBusinessAction("EVALUACION_SOLICITUD_AUSENCIA", "GestionAusencias", {
                    dniEditado: solicitud.empleadoDni,
                    cambiosRealizados: {
                        estado: { anterior: "pendiente", nuevo: "rechazado" },
                        motivoRechazo: { anterior: null, nuevo: motivoRechazo }
                    },
                    tipoAusencia: solicitud.tipo,
                    idSolicitud: solicitud.id
                }, usuario);
            } catch (e) {
                console.warn("Error al registrar auditoría:", e);
            }

            Swal.fire({
                title: "Rechazada",
                text: "Solicitud rechazada correctamente",
                icon: "success",
                timer: 2000,
                showConfirmButton: false
            });
            cargarSolicitudes();
            setShowModal(false);
            setMotivoRechazo("");
        } catch (error) {
            console.error(error);
            Swal.fire("Error", "No se pudo rechazar la solicitud", "error");
        } finally {
            setProcesando(false);
        }
    };

    const aprobarSeleccionados = async () => {
        const list = solicitudes.filter(s => seleccionados.has(s.id));
        if (list.length === 0) return;

        const result = await Swal.fire({
            title: '¿Confirmar Aprobación Masiva?',
            text: `Se aprobarán ${list.length} solicitudes seleccionadas.`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Sí, aprobar',
            confirmButtonColor: '#28a745',
            cancelButtonText: 'Cancelar'
        });

        if (!result.isConfirmed) return;

        setProcesando(true);
        const batch = writeBatch(db);
        const rucEmpresa = usuario.ruc || usuario.rucEmpresa;

        try {
            list.forEach(sol => {
                const ref = doc(db, "solicitudes_ausencia", sol.id);
                batch.update(ref, {
                    estado: "aprobado",
                    aprobadoPor: usuario.email,
                    fechaAprobacion: serverTimestamp()
                });
            });

            await batch.commit();

            list.forEach(sol => {
                notificarAprobacion(sol.empleadoDni, sol.nombreEmpleado, sol.tipo, usuario.email, rucEmpresa).catch(console.error);
            });

            // AUDITORÍA MASIVA
            try {
                await logBusinessAction("EVALUACION_SOLICITUD_AUSENCIA", "GestionAusencias", {
                    cambiosRealizados: {
                        aprobacionMasiva: { anterior: "Pendientes", nuevo: `Aprobadas ${list.length} solicitudes` }
                    },
                    dnisAfectados: list.map(s => s.empleadoDni)
                }, usuario);
            } catch (e) {
                console.warn("Error al registrar auditoría:", e);
            }

            Swal.fire("Éxito", `Se aprobaron ${list.length} solicitudes`, "success");
            cargarSolicitudes();
            setSeleccionados(new Set());
            setSeleccionarTodos(false);
        } catch (error) {
            console.error(error);
            Swal.fire("Error", "Falló la aprobación masiva", "error");
        } finally {
            setProcesando(false);
        }
    };

    const rechazarSeleccionados = async () => {
        const list = solicitudes.filter(s => seleccionados.has(s.id));
        if (list.length === 0) return;

        const { value: motivo } = await Swal.fire({
            title: "Rechazar Seleccionados",
            input: "textarea",
            inputLabel: "Ingrese el motivo de rechazo para todas las solicitudes seleccionadas",
            inputPlaceholder: "Escriba el motivo aquí...",
            showCancelButton: true,
            confirmButtonText: "Rechazar Todo",
            confirmButtonColor: "#d33",
            cancelButtonText: "Cancelar",
            inputValidator: (value) => {
                if (!value) return "Debe escribir un motivo";
            }
        });

        if (!motivo) return;

        setProcesando(true);
        const batch = writeBatch(db);
        const rucEmpresa = usuario.ruc || usuario.rucEmpresa;

        try {
            list.forEach(sol => {
                const ref = doc(db, "solicitudes_ausencia", sol.id);
                batch.update(ref, {
                    estado: "rechazado",
                    rechazadoPor: usuario.email,
                    fechaRechazo: serverTimestamp(),
                    motivoRechazo: motivo
                });
            });

            await batch.commit();

            list.forEach(sol => {
                notificarRechazo(sol.empleadoDni, sol.nombreEmpleado, sol.tipo, usuario.email, motivo, rucEmpresa).catch(console.error);
            });

            // AUDITORÍA MASIVA
            try {
                await logBusinessAction("EVALUACION_SOLICITUD_AUSENCIA", "GestionAusencias", {
                    cambiosRealizados: {
                        rechazoMasivo: { anterior: "Pendientes", nuevo: `Rechazadas ${list.length} solicitudes` },
                        motivoRechazo: { anterior: null, nuevo: motivo }
                    },
                    dnisAfectados: list.map(s => s.empleadoDni)
                }, usuario);
            } catch (e) {
                console.warn("Error al registrar auditoría:", e);
            }

            Swal.fire("Éxito", `Se rechazaron ${list.length} solicitudes`, "success");
            cargarSolicitudes();
            setSeleccionados(new Set());
            setSeleccionarTodos(false);
        } catch (error) {
            console.error(error);
            Swal.fire("Error", "Falló el rechazo masivo", "error");
        } finally {
            setProcesando(false);
        }
    };

    const verDetalle = (solicitud) => {
        setSolicitudSeleccionada(solicitud);
        setMotivoRechazo("");
        setShowModal(true);
    };

    const formatearFecha = (fecha) => {
        if (!fecha) return "-";
        const date = fecha.toDate ? fecha.toDate() : new Date(fecha);
        return date.toLocaleDateString('es-PE');
    };

    return (
        <div
            className="min-vh-100"
            style={{
                background: "linear-gradient(135deg, #ffffffff 0%, #ffffffff 100%)",
                minHeight: "100vh",
                width: "100vw",
                maxWidth: "100%",
                padding: "20px 0"
            }}
        >
            <Container fluid style={{ width: "90%", maxWidth: "100%" }}>
                {/* Main Card */}
                <Card
                    className="shadow-lg border-0"
                    style={{
                        borderRadius: "15px",
                        overflow: "hidden",
                        minHeight: "90vh" // Consistent height
                    }}
                >
                    {/* Header */}
                    <Card.Header
                        className="text-white py-4 position-relative position-relative"
                        style={{
                            background: "linear-gradient(45deg, #2c3e50, #3498db)",
                            borderBottom: "3px solid #f39c12"
                        }}
                    >
          <div
            className="position-absolute end-0 top-50 translate-middle-y me-4"
            style={{ zIndex: 10 }}
          >
            <BotonManualHeader />
          </div>
                        <div className="text-center">
                            <h2 className="fw-bold mb-1 d-inline-flex align-items-center">
                                <FaUserCheck className="me-3" />
                                Administración de Ausencias
                            </h2>
                            <p className="mb-0 opacity-75">
                                Gestione aprobaciones y rechazos de solicitudes
                            </p>
                        </div>

                        <div className="d-flex gap-2 position-absolute top-50 end-0 translate-middle-y me-4">
                            {seleccionados.size > 0 && (
                                <div className="d-flex gap-2 animate__animated animate__fadeIn">
                                    <Button
                                        variant="success"
                                        onClick={aprobarSeleccionados}
                                        disabled={procesando}
                                        className="shadow-sm border-0 d-flex align-items-center"
                                        style={{ borderRadius: "10px" }}
                                    >
                                        <FaCheckDouble className="me-2" /> Aprobar ({seleccionados.size})
                                    </Button>
                                    <Button
                                        variant="danger"
                                        onClick={rechazarSeleccionados}
                                        disabled={procesando}
                                        className="shadow-sm border-0 d-flex align-items-center"
                                        style={{ borderRadius: "10px" }}
                                    >
                                        <FaBan className="me-2" /> Rechazar ({seleccionados.size})
                                    </Button>
                                </div>
                            )}
                            <Button
                                variant="light"
                                onClick={cargarSolicitudes}
                                disabled={loading}
                                className="shadow-sm border-0 text-primary fw-bold"
                                style={{ borderRadius: "10px" }}
                            >
                                {loading ? <Spinner size="sm" animation="border" /> : <FaSync className={loading ? "fa-spin" : ""} />}
                            </Button>
                        </div>
                    </Card.Header>

                    <Card.Body className="p-4" style={{ backgroundColor: "#f8f9fa" }}>

                        {/* Filtros */}
                        <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: "15px" }}>
                            <Card.Body className="p-4">
                                <h5 className="fw-bold mb-3 text-secondary">
                                    <FaFilter className="me-2" />
                                    Filtros de Búsqueda
                                </h5>
                                <Row className="g-3">
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold text-muted small">ESTADO SOLICITUD</Form.Label>
                                            <Form.Select
                                                value={filtros.estado}
                                                onChange={e => setFiltros({ ...filtros, estado: e.target.value })}
                                                style={{ borderRadius: "10px" }}
                                            >
                                                <option value="pendiente">⏳ Pendientes</option>
                                                <option value="aprobado">✅ Aprobados</option>
                                                <option value="rechazado">❌ Rechazados</option>
                                                <option value="">🔍 Todos</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold text-muted small">DESDE FECHA</Form.Label>
                                            <Form.Control
                                                type="date"
                                                value={filtros.fechaDesde}
                                                onChange={e => setFiltros({ ...filtros, fechaDesde: e.target.value })}
                                                style={{ borderRadius: "10px" }}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold text-muted small">BUSCAR EMPLEADO</Form.Label>
                                            <div className="input-group">
                                                <span className="input-group-text bg-white border-end-0" style={{ borderTopLeftRadius: "10px", borderBottomLeftRadius: "10px" }}>
                                                    <FaSearch className="text-muted" />
                                                </span>
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Nombre del colaborador..."
                                                    value={filtros.empleado}
                                                    onChange={e => setFiltros({ ...filtros, empleado: e.target.value })}
                                                    className="border-start-0"
                                                    style={{ borderTopRightRadius: "10px", borderBottomRightRadius: "10px" }}
                                                />
                                            </div>
                                        </Form.Group>
                                    </Col>
                                    <Col md={2} className="d-flex align-items-end justify-content-end">
                                        <div className="text-end text-muted small">
                                            Total: <Badge bg="secondary" pill>{solicitudes.length}</Badge>
                                        </div>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        {/* Tabla */}
                        <Card className="shadow-sm border-0" style={{ borderRadius: "15px", overflow: "hidden" }}>
                            <Card.Body className="p-0">
                                {loading ? (
                                    <div className="text-center p-5">
                                        <Spinner animation="border" variant="primary" style={{ width: "3rem", height: "3rem" }} />
                                        <p className="mt-3 text-muted">Cargando solicitudes...</p>
                                    </div>
                                ) : (
                                    <Table hover responsive className="mb-0 align-middle">
                                        <thead className="bg-light text-secondary">
                                            <tr>
                                                <th className="py-3 ps-4" style={{ width: '50px' }}>
                                                    <Form.Check
                                                        type="checkbox"
                                                        checked={seleccionarTodos}
                                                        onChange={toggleSeleccionarTodos}
                                                        disabled={solicitudes.filter(s => s.estado === 'pendiente').length === 0}
                                                        className="custom-checkbox"
                                                    />
                                                </th>
                                                <th className="py-3">Colaborador</th>
                                                <th className="py-3">Tipo Ausencia</th>
                                                <th className="py-3">Periodo</th>
                                                <th className="py-3">Motivo</th>
                                                <th className="py-3">Estado</th>
                                                <th className="py-3 pe-4 text-end">Acciones</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {solicitudes.length === 0 ? (
                                                <tr>
                                                    <td colSpan="7" className="text-center py-5 text-muted">
                                                        <div className="d-flex flex-column align-items-center">
                                                            <FaClipboardList size={40} className="mb-3 text-secondary opacity-50" />
                                                            <h5>No se encontraron solicitudes</h5>
                                                            <p className="mb-0 small">No hay registros que coincidan con los filtros aplicados.</p>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ) : (
                                                solicitudes.map(sol => (
                                                    <tr key={sol.id} style={{ transition: "all 0.2s" }} className="hover-shadow">
                                                        <td className="ps-4">
                                                            {sol.estado === 'pendiente' && (
                                                                <Form.Check
                                                                    type="checkbox"
                                                                    checked={seleccionados.has(sol.id)}
                                                                    onChange={() => toggleSeleccion(sol.id)}
                                                                />
                                                            )}
                                                        </td>
                                                        <td>
                                                            <div className="fw-bold text-dark">{sol.nombreEmpleado}</div>
                                                            <div className="text-muted small">DNI: {sol.empleadoDni}</div>
                                                        </td>
                                                        <td>
                                                            <Badge bg="info" className="text-dark bg-opacity-25 border border-info px-3 py-2 rounded-pill">
                                                                {TIPO_LABELS[sol.tipo] || sol.tipo}
                                                            </Badge>
                                                        </td>
                                                        <td>
                                                            <div className="small">
                                                                <div className="fw-semibold text-primary">{formatearFecha(sol.fechaInicio)}</div>
                                                                <div className="text-muted">hasta {formatearFecha(sol.fechaFin)}</div>
                                                            </div>
                                                        </td>
                                                        <td style={{ maxWidth: '200px' }}>
                                                            <OverlayTrigger placement="top" overlay={<Tooltip>{sol.motivo}</Tooltip>}>
                                                                <span className="d-inline-block text-truncate w-100 text-secondary">
                                                                    {sol.motivo}
                                                                </span>
                                                            </OverlayTrigger>
                                                        </td>
                                                        <td>
                                                            <Badge bg={
                                                                sol.estado === 'pendiente' ? 'warning' :
                                                                    sol.estado === 'aprobado' ? 'success' : 'danger'
                                                            } className="px-3 py-2 rounded-pill text-uppercase shadow-sm">
                                                                {sol.estado === 'pendiente' && "⏳ "}
                                                                {sol.estado === 'aprobado' && "✅ "}
                                                                {sol.estado === 'rechazado' && "❌ "}
                                                                {sol.estado}
                                                            </Badge>
                                                        </td>
                                                        <td className="pe-4 text-end">
                                                            <Button
                                                                variant="outline-primary"
                                                                size="sm"
                                                                onClick={() => verDetalle(sol)}
                                                                className="rounded-pill px-3"
                                                            >
                                                                <FaEye className="me-1" /> Detalle
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </Table>
                                )}
                            </Card.Body>
                        </Card>
                    </Card.Body>
                </Card>

                {/* MODAL DETALLE */}
                <Modal
                    show={showModal}
                    onHide={() => setShowModal(false)}
                    size="lg"
                    centered
                    backdrop="static"
                >
                    <Modal.Header closeButton className="bg-light border-0">
                        <Modal.Title className="fw-bold text-primary">
                            <FaClipboardList className="me-2" />
                            Detalle de Solicitud
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="p-4">
                        {solicitudSeleccionada && (
                            <Row className="g-3">
                                <Col md={12}>
                                    <div className="p-3 bg-primary bg-opacity-10 rounded-3 d-flex justify-content-between align-items-center mb-2" style={{ borderLeft: "5px solid #0d6efd" }}>
                                        <div>
                                            <h5 className="fw-bold mb-1 text-primary">{solicitudSeleccionada.nombreEmpleado}</h5>
                                            <div className="text-muted small">DNI: {solicitudSeleccionada.empleadoDni}</div>
                                        </div>
                                        <Badge bg="light" text="dark" className="border shadow-sm px-3 py-2 fs-6">
                                            {(TIPO_LABELS[solicitudSeleccionada.tipo] || solicitudSeleccionada.tipo).toUpperCase()}
                                        </Badge>
                                    </div>
                                </Col>

                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label className="small fw-bold text-muted">FECHA INICIO</Form.Label>
                                        <Form.Control
                                            type="text"
                                            readOnly
                                            value={formatearFecha(solicitudSeleccionada.fechaInicio)}
                                            className="bg-light fw-bold"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label className="small fw-bold text-muted">FECHA FIN</Form.Label>
                                        <Form.Control
                                            type="text"
                                            readOnly
                                            value={formatearFecha(solicitudSeleccionada.fechaFin)}
                                            className="bg-light fw-bold"
                                        />
                                    </Form.Group>
                                </Col>

                                <Col md={12}>
                                    <Form.Group>
                                        <Form.Label className="small fw-bold text-muted">OBSERVACIONES / MOTIVO</Form.Label>
                                        <div className="p-3 bg-light rounded border text-secondary" style={{ minHeight: "80px" }}>
                                            {solicitudSeleccionada.motivo}
                                        </div>
                                    </Form.Group>
                                </Col>

                                {solicitudSeleccionada.pdfUrl && (
                                    <Col md={12}>
                                        <div className="d-grid">
                                            <a
                                                href={solicitudSeleccionada.pdfUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="btn btn-outline-info rounded-pill"
                                            >
                                                📄 Ver Documento Adjunto
                                            </a>
                                        </div>
                                    </Col>
                                )}

                                {(solicitudSeleccionada.firma_url || solicitudSeleccionada.firmaEmpleadoUrl) && (
                                    <Col md={12}>
                                        <Form.Label className="small fw-bold text-muted d-block">FIRMA DEL TRABAJADOR (APP)</Form.Label>
                                        <div className="text-center p-3 border rounded bg-white">
                                            <img 
                                                src={solicitudSeleccionada.firma_url || solicitudSeleccionada.firmaEmpleadoUrl} 
                                                alt="Firma del trabajador" 
                                                style={{ maxHeight: '100px', maxWidth: '100%' }}
                                            />
                                        </div>
                                    </Col>
                                )}

                                {solicitudSeleccionada.estado === "pendiente" && (
                                    <Col md={12} className="mt-4">
                                        <Form.Group className="border-top pt-3">
                                            <Form.Label className="fw-bold text-danger">
                                                <FaTimes className="me-1" />
                                                Motivo de Rechazo (Opcional - solo si rechaza)
                                            </Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={2}
                                                placeholder="Escriba la razón del rechazo..."
                                                value={motivoRechazo}
                                                onChange={e => setMotivoRechazo(e.target.value)}
                                                className="border-danger"
                                            />
                                        </Form.Group>
                                    </Col>
                                )}

                                {solicitudSeleccionada.estado === "rechazado" && solicitudSeleccionada.motivoRechazo && (
                                    <Col md={12}>
                                        <Alert variant="danger">
                                            <strong>Motivo de rechazo:</strong> {solicitudSeleccionada.motivoRechazo}
                                        </Alert>
                                    </Col>
                                )}
                            </Row>
                        )}
                    </Modal.Body>
                    <Modal.Footer className="bg-light border-0">
                        <Button variant="outline-secondary" onClick={() => setShowModal(false)} className="rounded-pill px-4">
                            Cerrar
                        </Button>
                        {solicitudSeleccionada?.estado === "pendiente" && (
                            <>
                                <Button
                                    variant="danger"
                                    disabled={procesando}
                                    onClick={() => rechazarSolicitud(solicitudSeleccionada)}
                                    className="rounded-pill px-4 shadow-sm"
                                >
                                    {procesando ? <Spinner size="sm" animation="border" /> : <><FaBan className="me-2" /> Rechazar</>}
                                </Button>
                                <Button
                                    variant="success"
                                    disabled={procesando}
                                    onClick={() => aprobarSolicitud(solicitudSeleccionada)}
                                    className="rounded-pill px-4 shadow-sm"
                                >
                                    {procesando ? <Spinner size="sm" animation="border" /> : <><FaCheck className="me-2" /> Aprobar</>}
                                </Button>
                            </>
                        )}
                    </Modal.Footer>
                </Modal>
            </Container>
        </div>
    );
}

export default AdminAusencias;
